import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useIsMobile } from "../hooks/useIsMobile";
import { useLiveActivity } from "../hooks/useLiveActivity";
import { track } from "../services/analytics";
import {
  getLead,
  getLeadFollowUpCounts,
  getLeadPriorityCounts,
  getLeadStatusCounts,
  getLeads,
  getLeadsByCategory,
  getLeadsByType,
  getLocations,
  getNeedsCallbackCount,
  getUnreadLeadsCount,
  getUnreadMessagesCount,
  getWaitingReplies,
  markLeadAsRead,
  updateLead,
} from "../services/api.js";
import { formatDate } from "../utils";
import {
  LEAD_SOURCES,
  LEAD_STATUSES,
  formatAiNextAction,
  formatLeadCategory,
  formatLeadStatus,
  formatLeadType,
  formatLeadTypeToIcon,
  formatLossReason,
  formatPhoneNumber,
  getAiNextActionColor,
  getCallOutcomeDisplay,
  getLeadCategoryColor,
  getLeadStatusColor,
} from "../utils/displayFormatters";
import AiPatientNameModal from "./AiPatientNameModal";
import ColumnSelector from "./ColumnSelector";
import LeadCard from "./LeadCard";
import LeadDetail from "./LeadDetail";
import LeadListFiltersSheet from "./LeadListFiltersSheet";
import LeadListSidebar from "./LeadListSidebar";
import Pagination from "./Pagination";
import ProspectsToggle from "./ProspectsToggle";
import UnreadToggle from "./UnreadToggle";

// Date range defaults to "Last 30 days" but the user's last pick is remembered
// in localStorage ("global_date_range") so it carries between the Dashboard and
// this list. Homepage reads/writes the same key.
const getStoredActivityRange = () =>
  localStorage.getItem("global_date_range") || "30";

const LeadList = ({ authToken }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [filtersSheetOpen, setFiltersSheetOpen] = useState(false);
  const queryParams = new URLSearchParams(location.search);
  const locationSearchRef = useRef(location.search);
  locationSearchRef.current = location.search;
  const renderCountRef = useRef(0);
  const { activityVersion, clearNewCount } = useLiveActivity();
  const seenActivityVersion = useRef(activityVersion);
  renderCountRef.current += 1;
  console.log(`[LeadList] render #${renderCountRef.current}`, { search: location.search });

  const [showAiNameModal, setShowAiNameModal] = useState(
    () => localStorage.getItem("ai_patient_name_v1_seen") !== "true",
  );
  const [showListLayoutNotice, setShowListLayoutNotice] = useState(
    () => localStorage.getItem("list_layout_notice_v1") !== "true",
  );
  const dismissListLayoutNotice = () => {
    localStorage.setItem("list_layout_notice_v1", "true");
    setShowListLayoutNotice(false);
  };
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const [isSideCartOpen, setIsSideCartOpen] = useState(false);
  const [leadDetails, setLeadDetails] = useState(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [sideCartWidth, setSideCartWidth] = useState(window.innerWidth * 0.5);
  const [isResizing, setIsResizing] = useState(false);
  const [sideCartTitle, setSideCartTitle] = useState(<span>Lead Details</span>);
  const [leads, setLeads] = useState([]);
  const [isLoadingLeads, setIsLoadingLeads] = useState(false);
  const [leadStatusData, setLeadStatusData] = useState([]);
  const [leadTypeData, setLeadTypeData] = useState([]);
  const [sources, setSources] = useState([]);
  const [selectedSource, setSelectedSource] = useState(
    queryParams.get("source") || "",
  );
  const [selectedType, setSelectedType] = useState(
    queryParams.get("type") || "",
  );
  const [selectedStatus, setSelectedStatus] = useState(
    queryParams.get("status") || "",
  );
  const [selectedFollowUpStatus, setSelectedFollowUpStatus] = useState(
    queryParams.get("follow_up_status") || "",
  );
  const [selectedLastSeen, setSelectedLastSeen] = useState(
    queryParams.get("last_seen") || "",
  );
  const [selectedUnreadMessages, setSelectedUnreadMessages] = useState(
    queryParams.get("unread_messages") === "true",
  );
  const [selectedAppointmentDate, setSelectedAppointmentDate] = useState(
    queryParams.get("appointment_date") || "",
  );
  const [selectedActivityRange, setSelectedActivityRange] = useState(
    () => queryParams.get("days") || getStoredActivityRange(),
  );
  const [selectedActivityStartDate, setSelectedActivityStartDate] = useState(
    queryParams.get("start_date") || "",
  );
  const [selectedActivityEndDate, setSelectedActivityEndDate] = useState(
    queryParams.get("end_date") || "",
  );
  const [selectedPriority, setSelectedPriority] = useState(
    queryParams.get("ai_score") || "",
  );
  const [selectedUrgentOnly, setSelectedUrgentOnly] = useState(
    queryParams.get("ai_is_urgent") === "true",
  );
  // What the AI says to do next — the front desk's worklist ("everyone needing a call back").
  const [selectedNextAction, setSelectedNextAction] = useState(
    queryParams.get("next_action") || "",
  );
  const [selectedReadStatus, setSelectedReadStatus] = useState(
    queryParams.get("is_read_by_user") ||
      (localStorage.getItem("global_unread_only") === "true" ? "false" : ""),
  );
  // Derived view of the global "Unread only" toggle: ON ⇔ unread-scoped.
  const unreadOnly = selectedReadStatus === "false";
  // "Needs Callback" worklist: missed calls + answering-machine messages.
  const [selectedNeedsCallback, setSelectedNeedsCallback] = useState(
    queryParams.get("needs_callback") === "true",
  );
  const [needsCallbackCount, setNeedsCallbackCount] = useState(0);
  // Patients whose latest text no staff has answered.
  const [waitingReplyCount, setWaitingReplyCount] = useState(0);
  const [totalCount, setTotalCount] = useState(null);
  const [selectedWaitingReply, setSelectedWaitingReply] = useState(
    queryParams.get("waiting_reply") === "true",
  );
  // "Nobody here has opened it" — the dashboard's slipping-leads number links
  // in with ?untouched=true. Distinct from the per-user unread filter.
  const [selectedUntouched, setSelectedUntouched] = useState(
    queryParams.get("untouched") === "true",
  );
  // The dashboard's "Why leads were lost" rows link in with ?loss_reason=.
  const [selectedLossReason, setSelectedLossReason] = useState(
    queryParams.get("loss_reason") || "",
  );
  const [selectedCategory, setSelectedCategory] = useState(
    queryParams.get("lead_category") ||
      (localStorage.getItem("global_prospects_only") !== "false"
        ? "prospect"
        : ""),
  );
  // Derived view of the global "Prospects only" toggle: ON ⇔ prospect-scoped.
  const prospectsOnly = selectedCategory === "prospect";
  const [priorityCounts, setPriorityCounts] = useState({
    high_priority: 0,
    standard: 0,
    routine: 0,
    urgent: 0,
    no_analysis: 0,
  });
  const [categoryCounts, setCategoryCounts] = useState({});
  const [followUpCounts, setFollowUpCounts] = useState({ past_due: 0, due_today: 0, due_this_week: 0, due_later: 0, no_follow_up: 0 });
  const [unreadLeadsCount, setUnreadLeadsCount] = useState(0);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  const [sidebarUnreadRefresh, setSidebarUnreadRefresh] = useState(0);
  const [sortOrder, setSortOrder] = useState(
    queryParams.get("ordering") || "-last_seen_at",
  );
  const [currentPage, setCurrentPage] = useState(
    parseInt(queryParams.get("page")) || 1,
  );
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(
    parseInt(queryParams.get("page_size") || 15),
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [locations, setLocations] = useState([]);
  const [messagingEnabled, setMessagingEnabled] = useState(false);
  const [hoveredLeadId, setHoveredLeadId] = useState(null);
  const FOLLOW_UP_LABELS = {
    past_due: "Past Due",
    due_today: "Due Today",
    due_this_week: "Due This Week",
    due_later: "Due Later",
    no_follow_up: "No Reminder",
  };

  const PRIORITY_LABELS = {
    "3": "High Priority",
    "2": "Standard",
    "1": "Routine",
  };

  const READ_STATUS_LABELS = {
    true: "Read Only",
    false: "Unread Only",
  };

  // One definition of "a filter is on". The chips row, the Clear link and the
  // Mobile Filters badge all read it.
  const activeFilterCount = [
    searchQuery,
    selectedStatus,
    selectedType,
    selectedSource,
    selectedFollowUpStatus,
    selectedUnreadMessages,
    selectedPriority,
    selectedNextAction,
    selectedLossReason,
    selectedUrgentOnly,
    selectedNeedsCallback,
    selectedWaitingReply,
    selectedReadStatus,
    selectedCategory && selectedCategory !== "prospect",
    selectedAppointmentDate,
    selectedActivityStartDate,
    selectedActivityEndDate,
    selectedActivityRange && selectedActivityRange !== "30",
  ].filter(Boolean).length;
  const hasActiveChips = activeFilterCount > 0;

  const ACTIVITY_RANGE_OPTIONS = [
    { key: "1", label: "Today" },
    { key: "7", label: "Last 7 days" },
    { key: "30", label: "Last 30 days" },
    { key: "60", label: "Last 60 days" },
    { key: "90", label: "Last 90 days" },
    { key: "all", label: "All time" },
  ];

  const initialFields = [
    {
      key: "fullName",
      label: "Full Name",
      sortKey: "last_name",
      visible: true,
      width: "15%",
    },
    {
      key: "phone",
      label: "Phone",
      sortKey: "phone",
      visible: true,
      width: "10%",
    },
    {
      key: "lead_category",
      label: "Category",
      sortKey: "lead_category",
      visible: true,
      width: "9%",
    },
    {
      key: "status",
      label: "Status",
      sortKey: "status",
      visible: true,
      width: "13%",
    },
    {
      key: "ai_score",
      label: "Priority",
      sortKey: "ai_score",
      visible: false,
      width: "8%",
    },
    {
      // Effective next action: a staff override if set, else the AI's reading.
      // No sortKey — ordering on the coalesced value would need a viewset
      // annotation, and the sidebar filter already answers "only call backs".
      key: "next_action",
      label: "Next",
      visible: true,
      width: "10%",
    },
    {
      key: "ai_summary",
      label: "AI Summary",
      sortKey: "ai_analyzed_at",
      visible: true,
      width: "38%",
    },
    {
      key: "last_seen_at",
      label: "Last Seen",
      sortKey: "last_seen_at",
      visible: true,
      width: "12%",
    },
    {
      key: "updated_at",
      label: "Last Update",
      sortKey: "updated_at",
      visible: false,
      width: "12%",
    },
    {
      key: "source",
      label: "Source",
      sortKey: "source",
      visible: true,
      width: "9%",
    },
    {
      key: "loss_reason",
      label: "Loss Reason",
      sortKey: "loss_reason",
      visible: false,
      width: "10%",
    },
    {
      key: "insurance_provider",
      label: "Insurance Provider",
      sortKey: "insurance_provider",
      visible: false,
      width: "12%",
    },
    {
      key: "location",
      label: "Location",
      sortKey: "location",
      visible: false,
      width: "10%",
    },
    {
      key: "follow_up_date",
      label: "Reminder",
      sortKey: "follow_up_date",
      visible: true,
      width: "10%",
    },
    {
      key: "estimated_value",
      label: "Estimated Value",
      sortKey: "estimated_value",
      visible: false,
      width: "10%",
    },
  ];

  const [fields, setFields] = useState(() => {
    const savedFields = localStorage.getItem("leadListFields_v4");
    let updatedFields = initialFields;

    if (savedFields) {
      try {
        const parsedFields = JSON.parse(savedFields);
        updatedFields = initialFields.map((initialField) => {
          const savedField = parsedFields.find(
            (field) => field.key === initialField.key,
          );
          return savedField
            ? { ...initialField, visible: savedField.visible }
            : initialField;
        });
      } catch (error) {
        console.error("Error parsing saved fields:", error);
        updatedFields = initialFields;
      }
    }

    localStorage.setItem("leadListFields_v4", JSON.stringify(updatedFields));
    return updatedFields;
  });

  const transformLeadStatusData = (data) => {
    return data
      .filter((item) => LEAD_STATUSES.some((s) => s.key === item.name?.toLowerCase()))
      .map((item) => {
        const statusObj = LEAD_STATUSES.find((s) => s.key === item.name?.toLowerCase());
        return { ...item, key: statusObj.key, label: statusObj.label };
      });
  };

  const handleResetFilters = () => {
    setSelectedStatus("");
    setSelectedType("");
    setSelectedSource("");
    setSelectedFollowUpStatus("");
    setSelectedLastSeen("");
    setSelectedActivityRange("30");
    setSelectedActivityStartDate("");
    setSelectedActivityEndDate("");
    setSelectedUnreadMessages(false);
    setSelectedAppointmentDate("");
    setSelectedPriority("");
    setSelectedUrgentOnly(false);
    setSelectedReadStatus("");
    setSelectedNeedsCallback(false);
    setSelectedWaitingReply(false);
    setSelectedLossReason("");
    // Prospects-only is the global default — keep it ON when clearing filters.
    setSelectedCategory("prospect");
    setSearchQuery("");
    setCurrentPage(1);
  };

  // Outcome badge shown on every phone-call row (Missed call / Message left /
  // Answered / …). Uses the shared formatter so the list and the lead detail
  // header always show the same label and color.
  const renderCallbackBadge = (lead) => {
    const display = getCallOutcomeDisplay(lead);
    if (!display) return null;
    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${display.className}`}
      >
        {display.label}
      </span>
    );
  };

  // The waiting-reply filter turned on, from the dashboard banner, the sidebar
  // or a restored URL.
  useEffect(() => {
    if (!selectedWaitingReply) return;
    track("waiting_replies_filter_used", {
      location_ids: locations
        .filter((location) => location.messaging_enabled)
        .map((location) => location.id),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWaitingReply]);

  // One click for "I called or texted back". It sets status to contacted only;
  // a waiting reply clears when staff text the patient.
  const handleMarkContacted = async (event, lead) => {
    event.preventDefault();
    event.stopPropagation();
    try {
      await updateLead(authToken, lead.id, { status: "contacted" });
      handleLeadUpdate(lead.id, { status: "contacted" });
    } catch (error) {
      console.error("Error marking lead contacted:", error);
    }
  };

  const renderWaitingReply = (lead) => {
    if (!lead.waiting_reply) return null;
    return (
      <>
        <span
          title="Text the patient back to clear this"
          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap bg-amber-100 text-amber-800"
        >
          Texted, waiting for reply
        </span>
        {/* Contacted moves a new lead on; on a later status it would move the lead back. */}
        {lead.status === "new" && (
          <button
            type="button"
            onClick={(event) => handleMarkContacted(event, lead)}
            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap bg-teal-700 text-white hover:bg-teal-800"
          >
            Contacted
          </button>
        )}
      </>
    );
  };

  useEffect(() => {
    localStorage.setItem("leadListFields_v4", JSON.stringify(fields));
  }, [fields]);

  useEffect(() => {
    // New activity refetches the list without the spinner, so rows, scroll and
    // the open sidecart stay put.
    const silent = seenActivityVersion.current !== activityVersion;
    seenActivityVersion.current = activityVersion;
    // Staff looking at the list have seen the activity: clear the tab title count.
    if (!document.hidden) clearNewCount();
    const fetchData = async () => {
      console.log(`[LeadList] fetchData fired — render #${renderCountRef.current}`, {
        selectedStatus, selectedType, selectedSource, selectedFollowUpStatus,
        selectedActivityRange, selectedUnreadMessages, selectedPriority,
        selectedUrgentOnly, selectedReadStatus, sortOrder, currentPage, pageSize, searchQuery,
      });
      if (!silent) setIsLoadingLeads(true);
      try {
        const activityDaysForCounts =
          selectedActivityStartDate || selectedActivityEndDate
            ? ""
            : selectedActivityRange;

        const [statusCounts, sourceCounts, priorityCountsData, categoryCountsData, followUpCountsData, response] = await Promise.all([
          getLeadStatusCounts(activityDaysForCounts, selectedActivityStartDate, selectedActivityEndDate, selectedCategory),
          getLeadsByType(activityDaysForCounts, selectedActivityStartDate, selectedActivityEndDate, selectedCategory),
          getLeadPriorityCounts(activityDaysForCounts, selectedActivityStartDate, selectedActivityEndDate, selectedCategory),
          getLeadsByCategory(activityDaysForCounts, selectedActivityStartDate, selectedActivityEndDate),
          getLeadFollowUpCounts(activityDaysForCounts, selectedActivityStartDate, selectedActivityEndDate, selectedCategory),
          getLeads(
            selectedStatus, selectedType, selectedSource, sortOrder, currentPage, pageSize,
            selectedFollowUpStatus, selectedLastSeen, selectedUnreadMessages, "", searchQuery,
            selectedAppointmentDate, selectedPriority, selectedUrgentOnly, selectedReadStatus,
            selectedActivityStartDate || selectedActivityEndDate ? "" : selectedActivityRange,
            selectedActivityStartDate, selectedActivityEndDate, selectedCategory,
            selectedNeedsCallback, selectedNextAction, selectedUntouched,
            selectedWaitingReply, selectedLossReason,
          ),
        ]);

        setLeadStatusData(transformLeadStatusData(statusCounts));
        setLeadTypeData(sourceCounts);
        setPriorityCounts(priorityCountsData);
        setCategoryCounts(Object.fromEntries((categoryCountsData || []).map(c => [c.name, c.value])));
        setFollowUpCounts(followUpCountsData || { past_due: 0, due_today: 0, due_this_week: 0, due_later: 0, no_follow_up: 0 });
        setLeads(response.results);
        setTotalPages(Math.ceil(response.count / pageSize));
        setTotalCount(response.count);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoadingLeads(false);
      }
    };
    fetchData();
  }, [
    selectedStatus,
    selectedType,
    selectedSource,
    selectedFollowUpStatus,
    selectedLastSeen,
    selectedActivityRange,
    selectedUnreadMessages,
    selectedAppointmentDate,
    selectedPriority,
    selectedNextAction,
    selectedUrgentOnly,
    selectedReadStatus,
    selectedNeedsCallback,
    selectedWaitingReply,
    selectedLossReason,
    selectedCategory,
    selectedActivityStartDate,
    selectedActivityEndDate,
    sortOrder,
    currentPage,
    pageSize,
    refreshTrigger,
    searchQuery,
    activityVersion,
  ]);

  // Fetch unread leads count
  useEffect(() => {
    const fetchUnreadLeadsCount = async () => {
      try {
        const count = await getUnreadLeadsCount(selectedCategory);
        setUnreadLeadsCount(count);
      } catch (error) {
        console.error("Failed to fetch unread leads count:", error);
      }
    };
    fetchUnreadLeadsCount();
  }, [refreshTrigger, selectedCategory, activityVersion]);

  // Fetch count of leads with unread messages (sidebar chip). Refetched on
  // refreshTrigger and the onMessagesRead backstop signal; opening a lead also
  // decrements it optimistically in handleOpenSideCart so it updates instantly.
  useEffect(() => {
    const fetchUnreadMessagesCount = async () => {
      try {
        const count = await getUnreadMessagesCount();
        setUnreadMessagesCount(count);
      } catch (error) {
        console.error("Failed to fetch unread messages count:", error);
      }
    };
    fetchUnreadMessagesCount();
  }, [refreshTrigger, sidebarUnreadRefresh, activityVersion]);

  // Fetch the "Needs Callback" worklist count for the sidebar chip
  useEffect(() => {
    const fetchNeedsCallbackCount = async () => {
      try {
        const count = await getNeedsCallbackCount(selectedCategory);
        setNeedsCallbackCount(count);
      } catch (error) {
        console.error("Failed to fetch needs-callback count:", error);
      }
    };
    fetchNeedsCallbackCount();
  }, [refreshTrigger, selectedCategory]);

  // Count for the sidebar chip, with the list's own date range and read filter
  // so it matches the rows shown after the click.
  useEffect(() => {
    const fetchWaitingReplyCount = async () => {
      try {
        const data = await getWaitingReplies({
          days:
            selectedActivityStartDate || selectedActivityEndDate
              ? ""
              : selectedActivityRange,
          startDate: selectedActivityStartDate,
          endDate: selectedActivityEndDate,
          leadCategory: selectedCategory,
          unreadOnly: selectedReadStatus === "false",
          pageSize: 1,
        });
        setWaitingReplyCount(data.count);
      } catch (error) {
        console.error("Failed to fetch waiting-reply count:", error);
      }
    };
    fetchWaitingReplyCount();
  }, [
    refreshTrigger,
    selectedCategory,
    selectedActivityRange,
    selectedActivityStartDate,
    selectedActivityEndDate,
    selectedReadStatus,
  ]);

  // Fetch locations to check messaging availability
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const locationsData = await getLocations();
        setLocations(locationsData);
        // Check if any location has messaging enabled
        const hasMessagingEnabled = locationsData.some(
          (location) => location.messaging_enabled,
        );
        setMessagingEnabled(hasMessagingEnabled);
      } catch (error) {
        console.error("Error fetching locations:", error);
        setMessagingEnabled(false);
      }
    };
    fetchLocations();
  }, []);

  const toggleFieldVisibility = (fieldKey) => {
    setFields((prevFields) =>
      prevFields.map((field) =>
        field.key === fieldKey ? { ...field, visible: !field.visible } : field,
      ),
    );
  };

  useEffect(() => {
    console.log(`[LeadList] Effect B (URL→state) fired — search="${location.search}"`);
    const params = new URLSearchParams(location.search);
    const getParam = (key, fallback = "") => params.get(key) ?? fallback;
    const getBool = (key) => params.get(key) === "true";

    setSelectedStatus(getParam("status", ""));
    setSelectedType(getParam("type", ""));
    setSelectedSource(getParam("source", ""));
    setSelectedFollowUpStatus(getParam("follow_up_status", ""));
    setSelectedLastSeen(getParam("last_seen", ""));
    setSelectedUnreadMessages(getBool("unread_messages"));
    setSelectedAppointmentDate(getParam("appointment_date", ""));
    setSelectedPriority(getParam("ai_score", ""));
    setSelectedNextAction(getParam("next_action", ""));
    setSelectedUrgentOnly(getBool("ai_is_urgent"));
    // No URL param → fall back to the global "Unread only" toggle, so it
    // carries over from the Dashboard the same way "Prospects only" does.
    setSelectedReadStatus(
      getParam("is_read_by_user", "") ||
        (localStorage.getItem("global_unread_only") === "true" ? "false" : ""),
    );
    setSelectedNeedsCallback(getParam("needs_callback", "") === "true");
    setSelectedWaitingReply(getParam("waiting_reply", "") === "true");
    setSelectedUntouched(getParam("untouched", "") === "true");
    setSelectedLossReason(getParam("loss_reason", ""));
    setSortOrder(getParam("ordering", "-last_seen_at"));
    setCurrentPage(parseInt(getParam("page", "1"), 10) || 1);
    setPageSize(parseInt(getParam("page_size", "15"), 10) || 15);

    const startDate = getParam("start_date", "");
    const endDate = getParam("end_date", "");
    const daysParam = getParam("days", "");

    if (startDate || endDate) {
      setSelectedActivityStartDate(startDate);
      setSelectedActivityEndDate(endDate);
    } else {
      setSelectedActivityStartDate("");
      setSelectedActivityEndDate("");
    }

    if (daysParam) {
      setSelectedActivityRange(daysParam);
    } else {
      setSelectedActivityRange(getStoredActivityRange());
    }
  }, [location.search]);

  useEffect(() => {
    localStorage.setItem("global_date_range", selectedActivityRange);
  }, [selectedActivityRange]);

  // Keep the global "Prospects only" toggle in sync so it carries to the Dashboard.
  useEffect(() => {
    localStorage.setItem(
      "global_prospects_only",
      String(selectedCategory === "prospect"),
    );
  }, [selectedCategory]);

  const handleProspectsOnlyChange = (nextValue) => {
    setSelectedCategory(nextValue ? "prospect" : "");
    setCurrentPage(1);
  };

  // Keep the global "Unread only" toggle in sync so it carries to the Dashboard.
  useEffect(() => {
    localStorage.setItem("global_unread_only", String(unreadOnly));
  }, [unreadOnly]);

  const handleUnreadOnlyChange = (nextValue) => {
    setSelectedReadStatus(nextValue ? "false" : "");
    setCurrentPage(1);
  };

  useEffect(() => {
    const newQueryParams = new URLSearchParams({
      status: selectedStatus,
      type: selectedType,
      source: selectedSource,
      follow_up_status: selectedFollowUpStatus,
      last_seen: selectedLastSeen,
      ...(selectedActivityStartDate ? { start_date: selectedActivityStartDate } : {}),
      ...(selectedActivityEndDate ? { end_date: selectedActivityEndDate } : {}),
      ...(selectedActivityStartDate || selectedActivityEndDate
        ? {}
        : selectedActivityRange
          ? { days: selectedActivityRange }
          : {}),
      unread_messages: selectedUnreadMessages.toString(),
      ...(selectedReadStatus !== "" ? { is_read_by_user: selectedReadStatus } : {}),
      ...(selectedNeedsCallback ? { needs_callback: "true" } : {}),
      ...(selectedWaitingReply ? { waiting_reply: "true" } : {}),
      ...(selectedUntouched ? { untouched: "true" } : {}),
      ...(selectedLossReason ? { loss_reason: selectedLossReason } : {}),
      ...(selectedCategory ? { lead_category: selectedCategory } : {}),
      appointment_date: selectedAppointmentDate,
      ai_score: selectedPriority,
      ...(selectedNextAction ? { next_action: selectedNextAction } : {}),
      ai_is_urgent: selectedUrgentOnly.toString(),
      ordering: sortOrder,
      page: currentPage.toString(),
      page_size: pageSize.toString(),
    });

    const existingParams = new URLSearchParams(locationSearchRef.current);
    const willNavigate = newQueryParams.toString() !== existingParams.toString();
    console.log(`[LeadList] Effect C (state→URL) fired — willNavigate=${willNavigate}`, {
      new: newQueryParams.toString(),
      existing: existingParams.toString(),
    });
    if (willNavigate) {
      navigate({
        pathname: "/leads",
        search: `?${newQueryParams.toString()}`,
      });
    }
  }, [
    selectedStatus,
    selectedType,
    selectedSource,
    selectedFollowUpStatus,
    selectedLastSeen,
    selectedActivityRange,
    selectedUnreadMessages,
    selectedAppointmentDate,
    selectedPriority,
    selectedNextAction,
    selectedUrgentOnly,
    selectedReadStatus,
    selectedNeedsCallback,
    selectedWaitingReply,
    selectedLossReason,
    selectedCategory,
    selectedActivityStartDate,
    selectedActivityEndDate,
    sortOrder,
    currentPage,
    pageSize,
    navigate,
  ]);

  useEffect(() => {
    if (selectedLeadId) {
      const fetchLeadDetails = async () => {
        setIsLoadingDetails(true);
        try {
          const lead = await getLead(authToken, selectedLeadId);
          setLeadDetails(lead);
          const fullName = `${lead.first_name || ""} ${
            lead.last_name || ""
          }`.trim();
          setSideCartTitle(fullName || "Lead Details");
        } catch (error) {
          console.error("Error fetching lead details:", error);
          setLeadDetails(null);
        } finally {
          setIsLoadingDetails(false);
        }
      };
      fetchLeadDetails();
    } else {
      setLeadDetails(null);
      setSideCartTitle("Lead Details");
    }
  }, [selectedLeadId, authToken, refreshTrigger]);

  const handleFilterChange = ({ status, type }) => {
    setSelectedStatus(status);
    setSelectedType(type);
    setCurrentPage(1);
  };

  const handleLeadUpdate = (leadId, updatedLead = null) => {
    // Optimistically reflect the edit in the visible row so the list updates
    // immediately, without waiting on (or racing) the background refetch.
    if (updatedLead) {
      setLeads((prevLeads) =>
        prevLeads.map((lead) =>
          lead.id === leadId ? { ...lead, ...updatedLead } : lead,
        ),
      );
    }
    setRefreshTrigger((prev) => prev + 1);
    if (leadId === selectedLeadId) {
      setSelectedLeadId(leadId);
    }
  };

  const handleOpenSideCart = async (leadId) => {
    // Mobile view: the lead is a full-screen page, not the sidecart.
    if (isMobile) {
      navigate(`/leads/${leadId}`, { state: { fromList: true } });
      return;
    }
    setSelectedLeadId(leadId);
    setIsSideCartOpen(true);

    // Opening the sidecart marks the lead's inbound messages as read (via the
    // hidden LeadMessaging). Optimistically drop this lead from the unread-
    // messages count so the sidebar chip updates instantly instead of relying
    // on the refetch chain. Zero the row's count so re-opening won't decrement
    // again. The refetch on refreshTrigger reconciles any drift.
    const openedLead = leads.find((lead) => lead.id === leadId);
    if (openedLead?.unread_message_count > 0) {
      setUnreadMessagesCount((prev) => Math.max(0, prev - 1));
      setLeads((prevLeads) =>
        prevLeads.map((lead) =>
          lead.id === leadId ? { ...lead, unread_message_count: 0 } : lead,
        ),
      );
    }

    // Mark lead as read when opening sidecart
    try {
      await markLeadAsRead(leadId);
      // Update local state to reflect read status
      setLeads((prevLeads) =>
        prevLeads.map((lead) =>
          lead.id === leadId ? { ...lead, is_read_by_user: true } : lead,
        ),
      );
      // Update unread count
      setUnreadLeadsCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Failed to mark lead as read:", error);
    }
  };;

  const handleCloseSideCart = () => {
    setSelectedLeadId(null);
    setIsSideCartOpen(false);
  };

  const handleSort = (sortKey) => {
    if (!sortKey) return;

    setSortOrder((current) => {
      if (current === sortKey) return `-${sortKey}`;
      if (current === `-${sortKey}`) return sortKey;
      return sortKey;
    });
  };

  const getPriorityBorderClass = (lead) => {
    if (lead.ai_is_urgent) return "border-l-4 border-red-500";
    if (lead.ai_score === 3) return "border-l-4 border-red-400";
    if (lead.ai_score === 2) return "border-l-4 border-amber-400";
    if (lead.ai_score === 1) return "border-l-4 border-blue-400";
    return "border-l-4 border-transparent";
  };

  const isPastDue = (followUpDate) => {
    if (!followUpDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const followUp = new Date(`${followUpDate}T00:00:00`);
    followUp.setHours(0, 0, 0, 0);
    return followUp < today && followUp.getTime() !== today.getTime();
  };

  const isDueToday = (followUpDate) => {
    if (!followUpDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const followUp = new Date(`${followUpDate}T00:00:00`);
    followUp.setHours(0, 0, 0, 0);
    return followUp.getTime() === today.getTime();
  };

  const handleMouseDown = (e) => {
    setIsResizing(true);
    document.body.style.cursor = "col-resize";
  };

  const handleMouseMove = (e) => {
    if (!isResizing) return;
    const newWidth = window.innerWidth - e.clientX;
    if (newWidth >= 300 && newWidth <= 1200) {
      setSideCartWidth(newWidth);
    }
  };

  const handleMouseUp = () => {
    setIsResizing(false);
    document.body.style.cursor = "default";
  };

  useEffect(() => {
    if (isResizing) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    }
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing]);

  const handleDismissAiNameModal = () => {
    localStorage.setItem("ai_patient_name_v1_seen", "true");
    setShowAiNameModal(false);
  };

  const renderSidebar = (layout) => (
    <LeadListSidebar
          layout={layout}
          leadTypeData={leadTypeData}
          leadStatusData={leadStatusData}
          selectedStatus={selectedStatus}
          selectedType={selectedType}
          selectedFollowUpStatus={selectedFollowUpStatus}
          selectedUnreadMessages={selectedUnreadMessages}
          messagingEnabled={messagingEnabled}
          onFilterChange={handleFilterChange}
          onFollowUpFilterChange={(followUpStatus) => {
            setSelectedFollowUpStatus(followUpStatus);
            setCurrentPage(1);
          }}
          onUnreadMessagesChange={(unreadMessages) => {
            setSelectedUnreadMessages(unreadMessages);
            setCurrentPage(1);
          }}
          selectedPriority={selectedPriority}
          onPriorityFilterChange={(priority) => {
            setSelectedPriority(priority);
            setCurrentPage(1);
          }}
          selectedNextAction={selectedNextAction}
          onNextActionFilterChange={(nextAction) => {
            setSelectedNextAction(nextAction);
            setCurrentPage(1);
          }}
          selectedUrgentOnly={selectedUrgentOnly}
          onUrgentOnlyChange={(urgentOnly) => {
            setSelectedUrgentOnly(urgentOnly);
            setCurrentPage(1);
          }}
          priorityCounts={priorityCounts}
          selectedReadStatus={selectedReadStatus}
          onReadStatusFilterChange={(readStatus) => {
            setSelectedReadStatus(readStatus);
            setCurrentPage(1);
          }}
          unreadLeadsCount={unreadLeadsCount}
          unreadMessagesCount={unreadMessagesCount}
          selectedNeedsCallback={selectedNeedsCallback}
          needsCallbackCount={needsCallbackCount}
          selectedWaitingReply={selectedWaitingReply}
          waitingReplyCount={waitingReplyCount}
          onWaitingReplyChange={(waitingReply) => {
            setSelectedWaitingReply(waitingReply);
            setCurrentPage(1);
          }}
          onNeedsCallbackChange={(needsCallback) => {
            setSelectedNeedsCallback(needsCallback);
            setCurrentPage(1);
          }}
          categoryCounts={categoryCounts}
          followUpCounts={followUpCounts}
          selectedCategory={selectedCategory}
          onCategoryFilterChange={(category) => {
            setSelectedCategory(category);
            setCurrentPage(1);
          }}
    />
  );

  return (
    <div className="w-full mx-auto px-0 sm:px-2 lg:px-0 pt-24 pb-6 bg-background">
      {showAiNameModal && (
        <AiPatientNameModal onDismiss={handleDismissAiNameModal} />
      )}
      <div className="flex flex-col sm:flex-row justify-between items-center mb-0">
        {/* Page title */}
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <div className="w-1.5 h-10 bg-primary rounded-full"></div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-text-primary">
                Active Leads
              </h1>
            </div>
          </div>
        </div>

        <Link
          to="/leads/create"
          className="bg-primary hover:brightness-125 text-white font-medium py-2 px-4 rounded-lg flex items-center space-x-2 transition duration-300"
          data-spotlight="create-lead-btn"
        >
          <svg
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4v16m8-8H4"
            />
          </svg>
          <span>Create New Lead</span>
        </Link>
      </div>

      <div className="flex flex-col lg:flex-row gap-6 items-start">
        {!isMobile && renderSidebar()}
        {isMobile && (
          <div className="flex w-full flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setFiltersSheetOpen(true)}
              className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2 text-sm font-medium text-text-primary"
            >
              Filters
              {activeFilterCount > 0 && (
                <span className="rounded-full bg-teal-700 px-2 py-0.5 text-xs text-white">
                  {activeFilterCount}
                </span>
              )}
            </button>
            {messagingEnabled &&
              (waitingReplyCount > 0 || selectedWaitingReply) && (
                <button
                  type="button"
                  aria-pressed={selectedWaitingReply}
                  onClick={() => {
                    setSelectedWaitingReply(!selectedWaitingReply);
                    setCurrentPage(1);
                  }}
                  className={`rounded-full border px-3 py-2 text-sm font-medium ${
                    selectedWaitingReply
                      ? "border-amber-600 bg-amber-600 text-white"
                      : "border-amber-200 bg-amber-50 text-amber-800"
                  }`}
                >
                  Waiting reply {waitingReplyCount}
                </button>
              )}
            {(needsCallbackCount > 0 || selectedNeedsCallback) && (
              <button
                type="button"
                aria-pressed={selectedNeedsCallback}
                onClick={() => {
                  setSelectedNeedsCallback(!selectedNeedsCallback);
                  setCurrentPage(1);
                }}
                className={`rounded-full border px-3 py-2 text-sm font-medium ${
                  selectedNeedsCallback
                    ? "border-red-600 bg-red-600 text-white"
                    : "border-red-200 bg-red-50 text-red-700"
                }`}
              >
                Needs callback {needsCallbackCount}
              </button>
            )}
            {hasActiveChips && (
              <button
                type="button"
                onClick={handleResetFilters}
                className="text-sm font-medium text-teal-700 underline"
              >
                Clear
              </button>
            )}
          </div>
        )}
        {isMobile && filtersSheetOpen && (
          <LeadListFiltersSheet
            leadCount={totalCount}
            onClose={() => setFiltersSheetOpen(false)}
          >
            {renderSidebar("sheet")}
          </LeadListFiltersSheet>
        )}

        <div className="flex-1 lg:w-64 bg-white rounded-lg shadow-sm p-4">
          {showListLayoutNotice && (
            <div className="mb-4 flex items-start gap-3 p-3.5 bg-teal-50 border border-teal-200 rounded-lg">
              <span className="text-lg flex-shrink-0 leading-none mt-0.5">
                💡
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-teal-800 mb-1">
                  Updated lead list
                </p>
                <ul className="text-xs text-teal-700 space-y-1">
                  <li className="flex items-start gap-1.5">
                    <span className="mt-0.5 w-2.5 h-2.5 rounded-sm bg-red-400 flex-shrink-0 inline-block" />
                    <span>
                      AI priority is now shown as a{" "}
                      <strong>colored left border</strong> on each row — red for
                      high, amber for standard, blue for routine.
                    </span>
                  </li>
                  <li className="flex items-start gap-1.5">
                    <span className="mt-0.5 w-2.5 h-2.5 rounded-full bg-teal-400 flex-shrink-0 inline-block" />
                    <span>
                      The <strong>Status column</strong> now also shows the lead
                      category (Existing Patient or Not Applicable) when it
                      differs from the default.
                    </span>
                  </li>
                </ul>
              </div>
              <button
                onClick={dismissListLayoutNotice}
                className="flex-shrink-0 text-teal-500 hover:text-teal-800 transition-colors p-0.5"
                aria-label="Dismiss"
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          )}
          <div className="mb-6">
            {/* Main Filter Bar */}
            <div className="flex flex-col gap-3 p-4 bg-white border border-border rounded-lg shadow-sm">
              {/* Search Input - Prominent */}
              <div className="relative w-full">
                <svg
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-text-secondary pointer-events-none"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <input
                  data-attr="lead-list-search"
                  type="text"
                  placeholder="Search by name, phone, or email..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full pl-10 pr-4 py-2.5 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm text-text-primary placeholder:text-text-secondary transition"
                />
                {searchQuery && (
                  <button
                    onClick={() => {
                      setSearchQuery("");
                      setCurrentPage(1);
                    }}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-text-secondary hover:text-primary"
                  >
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
              </div>

              <div className="grid grid-cols-3 gap-2 md:flex md:flex-wrap md:items-center">
                <div className="contents md:flex md:flex-wrap md:gap-2">
                  {ACTIVITY_RANGE_OPTIONS.map((option) => {
                    const isActive = option.key === selectedActivityRange;
                    return (
                      <button
                        key={option.key}
                        data-attr="lead-list-filter"
                        type="button"
                        onClick={() => {
                          setSelectedActivityRange(option.key);
                          setSelectedActivityStartDate("");
                          setSelectedActivityEndDate("");
                          setCurrentPage(1);
                        }}
                        className={`px-1 md:px-3 py-1.5 rounded-full text-[13px] md:text-sm font-medium whitespace-nowrap transition-colors border ${
                          isActive
                            ? "bg-teal-600 text-white border-teal-600"
                            : "bg-white text-gray-700 border-border hover:border-teal-300 hover:text-teal-700"
                        }`}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
                <div className="col-span-3 grid gap-2 md:contents [&>button]:w-full [&>button]:justify-between md:[&>button]:w-auto md:[&>button]:justify-start">
                  <ProspectsToggle
                    value={prospectsOnly}
                    onChange={handleProspectsOnlyChange}
                  />
                  <UnreadToggle
                    value={unreadOnly}
                    onChange={handleUnreadOnlyChange}
                  />
                </div>
                {hasActiveChips && (
                  <button
                    data-attr="lead-list-filter"
                    onClick={handleResetFilters}
                    className="col-span-3 px-3 py-2.5 text-sm text-primary hover:text-primary-dark font-medium hover:bg-accent-teal-subtle rounded-lg transition"
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            </div>

            {/* Active Filters Display - Optional but recommended */}
            {hasActiveChips ? (
              <div className="mt-3 flex items-center gap-2 flex-wrap px-4">
                <span className="text-xs font-medium text-text-secondary">
                  Active:
                </span>

                {searchQuery && (
                  <span className="ph-mask inline-flex items-center gap-1.5 px-2.5 py-1 bg-accent-teal-subtle text-primary rounded-full text-xs font-medium border border-primary/20">
                    Search: "{searchQuery.substring(0, 20)}
                    {searchQuery.length > 20 ? "..." : ""}"
                    <button
                      onClick={() => {
                        setSearchQuery("");
                        setCurrentPage(1);
                      }}
                      className="hover:bg-primary hover:text-white rounded-full p-0.5 transition"
                    >
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </span>
                )}

                {selectedActivityStartDate || selectedActivityEndDate ? (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-accent-teal-subtle text-primary rounded-full text-xs font-medium border border-primary/20">
                    Activity date: {selectedActivityStartDate || "Any"}
                    {selectedActivityEndDate
                      ? ` - ${selectedActivityEndDate}`
                      : ""}
                    <button
                      onClick={() => {
                        setSelectedActivityStartDate("");
                        setSelectedActivityEndDate("");
                      }}
                      className="hover:bg-primary hover:text-white rounded-full p-0.5 transition"
                    >
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </span>
                ) : null}

                {selectedActivityRange && selectedActivityRange !== "30" && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-accent-teal-subtle text-primary rounded-full text-xs font-medium border border-primary/20">
                    Date range:{" "}
                    {ACTIVITY_RANGE_OPTIONS.find(
                      (f) => f.key === selectedActivityRange,
                    )?.label || "Last 30 days"}
                    <button
                      onClick={() => setSelectedActivityRange("30")}
                      className="hover:bg-primary hover:text-white rounded-full p-0.5 transition"
                    >
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </span>
                )}

                {selectedStatus && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-accent-teal-subtle text-primary rounded-full text-xs font-medium border border-primary/20">
                    Status: {formatLeadStatus(selectedStatus).label}
                    <button
                      onClick={() => setSelectedStatus("")}
                      className="hover:bg-primary hover:text-white rounded-full p-0.5 transition"
                    >
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </span>
                )}

                {selectedCategory && selectedCategory !== "prospect" && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-accent-teal-subtle text-primary rounded-full text-xs font-medium border border-primary/20">
                    Category: {formatLeadCategory(selectedCategory).label}
                    <button
                      onClick={() => setSelectedCategory("")}
                      className="hover:bg-primary hover:text-white rounded-full p-0.5 transition"
                    >
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </span>
                )}

                {selectedType && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-accent-teal-subtle text-primary rounded-full text-xs font-medium border border-primary/20">
                    Type: {formatLeadType(selectedType)}
                    <button
                      onClick={() => setSelectedType("")}
                      className="hover:bg-primary hover:text-white rounded-full p-0.5 transition"
                    >
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </span>
                )}

                {selectedSource && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-accent-teal-subtle text-primary rounded-full text-xs font-medium border border-primary/20">
                    Source:{" "}
                    {LEAD_SOURCES.find((s) => s.key === selectedSource)
                      ?.label || selectedSource}
                    <button
                      onClick={() => setSelectedSource("")}
                      className="hover:bg-primary hover:text-white rounded-full p-0.5 transition"
                    >
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </span>
                )}

                {selectedFollowUpStatus && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-accent-teal-subtle text-primary rounded-full text-xs font-medium border border-primary/20">
                    Reminder:{" "}
                    {FOLLOW_UP_LABELS[selectedFollowUpStatus] ||
                      selectedFollowUpStatus}
                    <button
                      onClick={() => setSelectedFollowUpStatus("")}
                      className="hover:bg-primary hover:text-white rounded-full p-0.5 transition"
                    >
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </span>
                )}

                {selectedUnreadMessages && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-accent-teal-subtle text-primary rounded-full text-xs font-medium border border-primary/20">
                    Unread Messages
                    <button
                      onClick={() => setSelectedUnreadMessages(false)}
                      className="hover:bg-primary hover:text-white rounded-full p-0.5 transition"
                    >
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </span>
                )}

                {selectedLossReason && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-accent-teal-subtle text-primary rounded-full text-xs font-medium border border-primary/20">
                    Lost reason:{" "}
                    {selectedLossReason === "no_reason_specified"
                      ? "No reason specified"
                      : formatLossReason(selectedLossReason)}
                    <button
                      onClick={() => setSelectedLossReason("")}
                      className="hover:bg-primary hover:text-white rounded-full p-0.5 transition"
                    >
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </span>
                )}

                {selectedNextAction && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-accent-teal-subtle text-primary rounded-full text-xs font-medium border border-primary/20">
                    Next:{" "}
                    {formatAiNextAction(selectedNextAction) ||
                      selectedNextAction}
                    <button
                      onClick={() => setSelectedNextAction("")}
                      className="hover:bg-primary hover:text-white rounded-full p-0.5 transition"
                    >
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </span>
                )}

                {selectedPriority && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-accent-teal-subtle text-primary rounded-full text-xs font-medium border border-primary/20">
                    Priority:{" "}
                    {PRIORITY_LABELS[selectedPriority] || selectedPriority}
                    <button
                      onClick={() => setSelectedPriority("")}
                      className="hover:bg-primary hover:text-white rounded-full p-0.5 transition"
                    >
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </span>
                )}

                {selectedUrgentOnly && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-accent-teal-subtle text-primary rounded-full text-xs font-medium border border-primary/20">
                    Urgent Only
                    <button
                      onClick={() => setSelectedUrgentOnly(false)}
                      className="hover:bg-primary hover:text-white rounded-full p-0.5 transition"
                    >
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </span>
                )}

                {selectedWaitingReply && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-accent-teal-subtle text-primary rounded-full text-xs font-medium border border-primary/20">
                    Texted, waiting for reply
                    <button
                      onClick={() => setSelectedWaitingReply(false)}
                      className="hover:bg-primary hover:text-white rounded-full p-0.5 transition"
                    >
                      ×
                    </button>
                  </span>
                )}

                {selectedNeedsCallback && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-accent-teal-subtle text-primary rounded-full text-xs font-medium border border-primary/20">
                    Needs Callback
                    <button
                      onClick={() => setSelectedNeedsCallback(false)}
                      className="hover:bg-primary hover:text-white rounded-full p-0.5 transition"
                    >
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </span>
                )}

                {selectedReadStatus && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-accent-teal-subtle text-primary rounded-full text-xs font-medium border border-primary/20">
                    {READ_STATUS_LABELS[selectedReadStatus] ||
                      selectedReadStatus}
                    <button
                      onClick={() => setSelectedReadStatus("")}
                      className="hover:bg-primary hover:text-white rounded-full p-0.5 transition"
                    >
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </span>
                )}

                {selectedAppointmentDate && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-accent-teal-subtle text-primary rounded-full text-xs font-medium border border-primary/20">
                    Appointment: {selectedAppointmentDate}
                    <button
                      onClick={() => setSelectedAppointmentDate("")}
                      className="hover:bg-primary hover:text-white rounded-full p-0.5 transition"
                    >
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </span>
                )}
              </div>
            ) : null}
          </div>

          <div className="flex-1">
            <div className="bg-white shadow-sm rounded-lg relative">
              {isLoadingLeads ? (
                <div className="flex justify-center items-center py-20">
                  <div className="relative">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="h-3 w-3 bg-secondary rounded-full animate-pulse"></div>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {!isMobile && (
                    <div className="hidden sm:block">
                      <table className="min-w-full max-w-full divide-y divide-gray-200 table-fixed">
                        <colgroup>
                          {fields
                            .filter((field) => field.visible)
                            .map((field) => (
                              <col
                                key={field.key}
                                style={
                                  field.width
                                    ? { width: field.width }
                                    : undefined
                                }
                              />
                            ))}
                          <col style={{ width: "3%" }} />
                        </colgroup>
                        <thead className="bg-accent-teal-light-bg text-primary uppercase text-sm font-semibold tracking-wider border-b-2 border-primary">
                          <tr>
                            {fields
                              .filter((field) => field.visible)
                              .map((field) => (
                                <th
                                  key={field.key}
                                  onClick={() =>
                                    field.sortKey && handleSort(field.sortKey)
                                  }
                                  className={`px-2 py-3 text-center text-sm font-semibold text-teal-800 uppercase tracking-wider ${
                                    field.sortKey
                                      ? "cursor-pointer hover:bg-accent-teal-subtle select-none"
                                      : ""
                                  }`}
                                >
                                  <div className="flex items-center space-x-1">
                                    <span>{field.label}</span>
                                    {field.sortKey && (
                                      <span className="inline-block w-4">
                                        {sortOrder === field.sortKey && "↑"}
                                        {sortOrder === `-${field.sortKey}` &&
                                          "↓"}
                                      </span>
                                    )}
                                  </div>
                                </th>
                              ))}
                            <th className="px-2 py-3 text-left text-sm font-semibold text-teal-800 uppercase tracking-wider">
                              <div className="flex justify-between items-center z-50">
                                <ColumnSelector
                                  fields={fields}
                                  toggleFieldVisibility={toggleFieldVisibility}
                                />
                              </div>
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {leads.map((lead) => (
                            <tr
                              key={`lead-${lead.id}`}
                              onMouseEnter={() => setHoveredLeadId(lead.id)}
                              onMouseLeave={() => setHoveredLeadId(null)}
                              className={`transition duration-300 ${
                                lead.id === selectedLeadId
                                  ? "bg-accent-teal-subtle border-l-4 border-primary hover:bg-accent-teal-subtle"
                                  : `${getPriorityBorderClass(lead)} ${!lead.is_read_by_user ? "bg-blue-50/50 hover:bg-accent-teal-subtle/50" : "bg-white hover:bg-accent-teal-subtle/50"}`
                              }`}
                            >
                              {fields
                                .filter((field) => field.visible)
                                .map((field) => (
                                  <td
                                    key={`${lead.id}-${field.key}`}
                                    className="px-2 py-1 whitespace-normal text-gray-700 text-sm"
                                  >
                                    {field.key === "fullName" ? (
                                      <div className="ph-mask relative flex items-center space-x-2 py-2 px-3 rounded-md transition duration-200 w-full whitespace-nowrap">
                                        {!lead.is_read_by_user && (
                                          <span
                                            className="inline-flex items-center justify-center w-2 h-2 rounded-full bg-blue-500 flex-shrink-0"
                                            title="Unread"
                                          ></span>
                                        )}
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleOpenSideCart(lead.id);
                                          }}
                                          className={`text-primary hover:underline hover:text-primary ${
                                            !lead.is_read_by_user
                                              ? "font-bold"
                                              : "font-medium"
                                          }`}
                                        >
                                          {formatLeadTypeToIcon(lead.type)}{" "}
                                          {lead.first_name || ""}{" "}
                                          {lead.last_name || ""}
                                          <span className="text-gray-500 font-normal">
                                            ({lead.request_count})
                                          </span>
                                        </button>
                                        <button
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            handleOpenSideCart(lead.id);
                                          }}
                                          className={`absolute right-1 top-1/2 -translate-y-1/2 px-2 py-1 text-white text-xs rounded transition-opacity duration-200 ${
                                            hoveredLeadId === lead.id
                                              ? "opacity-100 bg-primary/60 hover:bg-primary/80"
                                              : "opacity-0 pointer-events-none"
                                          }`}
                                          data-ph-no-autocapture=""
                                          aria-label={`Preview ${
                                            lead.first_name || ""
                                          } ${lead.last_name || ""}`}
                                        >
                                          Preview
                                        </button>
                                        {isPastDue(lead.follow_up_date) && (
                                          <span
                                            className="inline-flex items-center justify-center w-2 h-2 rounded-full bg-red-300"
                                            title="Past Due"
                                          ></span>
                                        )}
                                        {isDueToday(lead.follow_up_date) && (
                                          <span
                                            className="inline-flex items-center justify-center w-2 h-2 rounded-full bg-yellow-300"
                                            title="Due Today"
                                          ></span>
                                        )}
                                        {lead.unread_message_count > 0 && (
                                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-accent-teal-subtle text-blue-800">
                                            {lead.unread_message_count} unread
                                          </span>
                                        )}
                                        {renderCallbackBadge(lead)}
                                        {renderWaitingReply(lead)}
                                      </div>
                                    ) : field.key === "status" ? (
                                      <span
                                        className={`px-2.5 py-0.5 inline-flex items-center justify-center text-xs font-medium rounded-full whitespace-nowrap ${getLeadStatusColor(lead.status)}`}
                                      >
                                        {formatLeadStatus(lead.status).label}
                                      </span>
                                    ) : field.key === "lead_category" ? (
                                      <span
                                        className={`px-2.5 py-0.5 inline-flex items-center justify-center text-xs font-medium rounded-full whitespace-nowrap ${getLeadCategoryColor(lead.lead_category)}`}
                                      >
                                        {
                                          formatLeadCategory(lead.lead_category)
                                            .label
                                        }
                                      </span>
                                    ) : field.key === "ai_score" ? (
                                      <div className="flex items-center justify-center">
                                        {lead.ai_score === 3 ? (
                                          <span className="px-2 py-0.5 text-xs font-medium rounded-sm bg-red-100 text-red-800 border border-red-200 whitespace-nowrap">
                                            {lead.ai_is_urgent
                                              ? "⚠ High"
                                              : "High"}
                                          </span>
                                        ) : lead.ai_score === 2 ? (
                                          <span className="px-2 py-0.5 text-xs font-medium rounded-sm bg-amber-100 text-amber-800 border border-amber-200 whitespace-nowrap">
                                            Mid
                                          </span>
                                        ) : lead.ai_score === 1 ? (
                                          <span className="px-2 py-0.5 text-xs font-medium rounded-sm bg-blue-100 text-blue-800 border border-blue-200 whitespace-nowrap">
                                            Low
                                          </span>
                                        ) : (
                                          <span className="text-gray-300 text-sm">
                                            —
                                          </span>
                                        )}
                                      </div>
                                    ) : field.key === "next_action" ? (
                                      <div className="flex items-center justify-center">
                                        {lead.effective_next_action ? (
                                          <span
                                            className={`px-2 py-0.5 text-xs font-medium rounded-sm whitespace-nowrap ${getAiNextActionColor(
                                              lead.effective_next_action,
                                            )}`}
                                            title={
                                              lead.next_action &&
                                              lead.ai_next_action &&
                                              lead.next_action !==
                                                lead.ai_next_action
                                                ? `Set by staff — AI said ${formatAiNextAction(
                                                    lead.ai_next_action,
                                                  )}`
                                                : undefined
                                            }
                                          >
                                            {formatAiNextAction(
                                              lead.effective_next_action,
                                            )}
                                          </span>
                                        ) : (
                                          <span className="text-gray-300 text-sm">
                                            —
                                          </span>
                                        )}
                                      </div>
                                    ) : field.key === "ai_summary" ? (
                                      <div
                                        className="ph-mask text-sm text-gray-700 max-w-md line-clamp-2"
                                        title={lead.ai_summary || ""}
                                      >
                                        {lead.ai_summary || (
                                          <span className="text-gray-400 italic">
                                            No summary available
                                          </span>
                                        )}
                                      </div>
                                    ) : field.key === "phone" ? (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleOpenSideCart(lead.id);
                                        }}
                                        className="ph-mask py-2 px-3 text-primary hover:text-primary hover:underline rounded-md transition duration-200 cursor-pointer w-full text-left transform whitespace-nowrap"
                                        data-ph-no-autocapture=""
                                        aria-label={`View details for phone number ${formatPhoneNumber(
                                          lead[field.key],
                                        )}`}
                                      >
                                        {formatPhoneNumber(lead[field.key])}
                                      </button>
                                    ) : field.key === "source" ? (
                                      <div className="text-sm text-gray-500">
                                        {lead.source_display}
                                      </div>
                                    ) : field.key === "last_seen_at" ||
                                      field.key === "updated_at" ? (
                                      <div className="text-sm text-gray-500 whitespace-nowrap w-auto">
                                        {lead[field.key]
                                          ? formatDate(lead[field.key], "short")
                                          : "N/A"}
                                      </div>
                                    ) : field.key === "follow_up_date" ? (
                                      <div className="text-sm text-gray-500">
                                        {formatDate(lead.follow_up_date, "iso")}
                                      </div>
                                    ) : field.key === "insurance_provider" ? (
                                      <div className="text-sm text-gray-500">
                                        {lead.insurance_provider || "N/A"}
                                      </div>
                                    ) : field.key === "location" ? (
                                      <div className="text-sm text-gray-500">
                                        {lead.location?.name || "N/A"}
                                      </div>
                                    ) : (
                                      <div className="text-sm text-gray-500">
                                        {lead[field.key] || "N/A"}
                                      </div>
                                    )}
                                  </td>
                                ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {isMobile && (
                    <div className="space-y-3">
                      {leads.map((lead) => (
                        <LeadCard
                          key={`lead-mobile-${lead.id}`}
                          lead={lead}
                          isSelected={lead.id === selectedLeadId}
                          badges={
                            <>
                              {renderCallbackBadge(lead)}
                              {renderWaitingReply(lead)}
                            </>
                          }
                        />
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {isSideCartOpen && (
            <div
              className="fixed top-0 bottom-0 right-0 bg-background shadow-xl transform transition-transform duration-300 ease-in-out z-[500] hidden sm:block"
              style={{
                width: `${sideCartWidth}px`,
                transform: isSideCartOpen
                  ? "translateX(0)"
                  : "translateX(100%)",
              }}
            >
              <div
                className="absolute left-0 top-0 h-full w-1.5 bg-primary/60 cursor-col-resize hover:bg-primary z-50"
                onMouseDown={handleMouseDown}
              />
              <div className="flex flex-col h-full">
                {/* <div className="flex items-center justify-between p-4 border-b border-border bg-primary">
                  <h2 className="text-xl font-semibold text-white">
                    {sideCartTitle}
                  </h2>
                  <button
                    onClick={handleCloseSideCart}
                    className="p-2 rounded-md text-white hover:text-gray-200 hover:bg-teal-600 transition-colors"
                    aria-label="Close"
                  >
                    <svg
                      className="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div> */}
                <div className="flex-1 overflow-y-auto px-6 pt-0 pb-0 relative">
                  {isLoadingDetails ? (
                    <div className="text-gray-600">Loading...</div>
                  ) : leadDetails ? (
                    <LeadDetail
                      lead={leadDetails}
                      authToken={authToken}
                      isSideCart={true}
                      onLeadUpdate={handleLeadUpdate}
                      setTitle={setSideCartTitle}
                      onClose={handleCloseSideCart}
                      onMessagesRead={() =>
                        setSidebarUnreadRefresh((n) => n + 1)
                      }
                    />
                  ) : (
                    <div className="text-red-600">
                      Failed to load lead details.
                    </div>
                  )}
                </div>
                <div className="p-4 border-t border-border">
                  <div className="flex space-x-3">
                    <Link
                      to={`/leads/${selectedLeadId}`}
                      className="inline-block border border-gray-400 text-white bg-primary hover:bg-secondary/90/40 hover:text-white rounded-lg px-4 py-1 opacity-100 text-sm font-medium transition duration-300"
                    >
                      View Full Details
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}

          <Pagination
            pageSize={pageSize}
            setPageSize={setPageSize}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalPages={totalPages}
            totalCount={totalCount}
          />
        </div>
      </div>
    </div>
  );
};;;

export default LeadList;
